import { STRINGS } from '@/strings/strings';
import type { StoryAudioStatus } from '@/features/story/types';

interface Props {
  status: StoryAudioStatus;
}

/**
 * Placeholder block for non-completed audio states. Renders the pending
 * ("Audio wird erstellt…"), retrying (same copy), failed ("Audio nicht
 * verfügbar", strikethrough), or nothing (`not_requested` → `null`).
 *
 * The `completed` state is NOT handled here — it belongs to
 * `StoryInlineAudioPlayer` which owns the full load / play / scrub UI.
 * Callers must branch on status before deciding which component to render
 * (typically: `status === 'completed' ? <StoryInlineAudioPlayer /> :
 * <AudioStatusPlaceholder />`).
 *
 * Renamed from `AudioPlaceholderButton` in Batch 6.2.2b when the completed
 * branch moved out — the old "placeholder button" no longer accurately
 * described a block that never produces a live CTA.
 */
export function AudioStatusPlaceholder({ status }: Props) {
  if (status === 'not_requested' || status === 'completed') return null;

  const copy = STRINGS.storyOutput.page.audio;

  if (status === 'failed') {
    return (
      <div
        aria-disabled
        className="h-11 w-full rounded-lg border border-error/40 bg-dream-background/40 text-center text-sm text-error line-through leading-[44px]"
      >
        {copy.failed}
      </div>
    );
  }

  // pending / generating / retrying
  return (
    <div
      aria-disabled
      aria-live="polite"
      className="h-11 w-full animate-pulse motion-reduce:animate-none rounded-lg bg-dream-gold text-center text-sm text-dream-navy leading-[44px]"
    >
      {copy.generating}
    </div>
  );
}
